import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LandingPage from '@/pages/LandingPage';
import Auth from '@/pages/Auth';
import Onboarding from '@/pages/Onboarding';
import Dashboard from '@/pages/Dashboard';
import AdminPanel from '@/pages/AdminPanel';
import MyClients from '@/pages/MyClients';
import BlogListPage from '@/pages/BlogListPage';
import BlogDetailPage from '@/pages/BlogDetailPage';
import DemoDashboard from '@/pages/DemoDashboard';
import VendorPanel from '@/pages/VendorPanel';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import PublicSnapshot from '@/pages/PublicSnapshot';
import PaymentSuccess from '@/pages/PaymentSuccess';
import TranslationPaymentSuccess from '@/pages/TranslationPaymentSuccess';
import PlanSelection from '@/pages/PlanSelection';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Terms from '@/pages/Terms';
import AboutUs from '@/pages/AboutUs';

function AppRoutes({ user, setUser, handleLogin, handleLogout }) {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState(null);

  // Load selected client from session storage on mount
  useEffect(() => {
    const savedClient = sessionStorage.getItem('selectedClient');
    if (savedClient && user && user.role !== 'client') {
      setSelectedClient(JSON.parse(savedClient));
    }
  }, [user]);

  // Handle client selection (for admin/internal team)
  const handleSelectClient = (firmId, firmName) => {
    const clientData = { firmId, name: firmName };
    setSelectedClient(clientData);
    sessionStorage.setItem('selectedClient', JSON.stringify(clientData));
    navigate('/dashboard/overview');
  };

  // Handle switching back to client list
  const handleSwitchClient = () => {
    setSelectedClient(null);
    sessionStorage.removeItem('selectedClient');
    if (user.role === 'super_admin') {
      navigate('/admin');
    } else {
      navigate('/my-clients');
    }
  };

  // Enhanced logout that clears client selection
  const handleFullLogout = () => {
    setSelectedClient(null);
    sessionStorage.removeItem('selectedClient');
    handleLogout();
  };

  // Determine the correct landing route based on role.
  // Plan-gating is handled INSIDE the dashboard (AccessGate), not here, so a
  // freshly-signed-up client with no plan still lands on /dashboard/overview
  // and sees the "Choose a plan to get started" blocker in the main area.

  const getDefaultRoute = () => {
    if (!user) return '/';
    
    // Translation vendor: dedicated portal regardless of role
    if (user.translation_vendor_access) {
      return '/vendor';
    }
    
    // Check onboarding only for clients
    if (user.role === 'client' && !user.firm?.onboarding_completed) {
      return '/onboarding';
    }
    
    switch (user.role) {
      case 'super_admin':
        return selectedClient ? '/dashboard/overview' : '/admin';
      case 'internal_team':
        return selectedClient ? '/dashboard/overview' : '/my-clients';
      case 'vendor':
        return '/vendor';
      case 'client':
      default:
        return '/dashboard/growth-snapshot';
    }
  };

  return (
    <Routes>
      {/* Landing Page */}
      <Route 
        path="/" 
        element={!user ? <LandingPage /> : <Navigate to={getDefaultRoute()} />} 
      />
      
      {/* Public Blog Routes */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogDetailPage />} />

      {/* Public Legal Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<AboutUs />} />
      
      {/* Demo Dashboard */}
      <Route path="/demo-dashboard" element={<DemoDashboard />} />

      {/* Public view-only Growth Snapshot (shareable with prospects) */}
      <Route path="/snapshot/:token" element={<PublicSnapshot />} />
      
      {/* Auth */}
      <Route 
        path="/auth" 
        element={!user ? <Auth onLogin={handleLogin} /> : <Navigate to={getDefaultRoute()} />} 
      />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify" element={<VerifyEmail />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/translation-payment-success" element={<TranslationPaymentSuccess />} />

      {/* Plan selection - always reachable for any authenticated client
          (entry gate AND the "Change Plan" flow). Non-clients go to their
          default route because they don't own billing. */}
      <Route
        path="/plan-selection"
        element={
          user && user.role === 'client'
            ? <PlanSelection onLogout={handleLogout} />
            : user ? <Navigate to={getDefaultRoute()} /> : <Navigate to="/auth" />
        }
      />
      
      {/* Onboarding (clients only) */}
      <Route 
        path="/onboarding" 
        element={
          user ? (
            user.role === 'client' && !user.firm?.onboarding_completed ? 
            <Onboarding user={user} /> : 
            <Navigate to={getDefaultRoute()} />
          ) : <Navigate to="/auth" />
        } 
      />
      
      {/* Super Admin Panel */}
      <Route 
        path="/admin" 
        element={
          user?.role === 'super_admin' && !user?.translation_vendor_access ? (
            <AdminPanel 
              user={user} 
              onLogout={handleFullLogout}
              onViewClientDashboard={handleSelectClient}
            />
          ) : (
            user ? <Navigate to={getDefaultRoute()} /> : <Navigate to="/auth" />
          )
        } 
      />
      
      {/* Internal Team - My Clients */}
      <Route 
        path="/my-clients" 
        element={
          (user?.role === 'internal_team' || user?.role === 'super_admin') && !user?.translation_vendor_access ? (
            <MyClients 
              user={user} 
              onLogout={handleFullLogout}
              onSelectClient={handleSelectClient}
            />
          ) : (
            user ? <Navigate to={getDefaultRoute()} /> : <Navigate to="/auth" />
          )
        } 
      />
      
      {/* Vendor Backend (translations) */}
      <Route 
        path="/vendor" 
        element={
          user?.translation_vendor_access || user?.role === 'vendor' ? (
            <VendorPanel user={user} onLogout={handleFullLogout} />
          ) : (
            user ? <Navigate to={getDefaultRoute()} /> : <Navigate to="/auth" />
          )
        } 
      />
      
      {/* Dashboard */}
      <Route 
        path="/dashboard/*" 
        element={
          user ? (
            // Translation vendor team members are walled off from the dashboard
            user.translation_vendor_access ? (
              <Navigate to="/vendor" />
            ) :
            // For clients, always show their own dashboard - the AccessGate
            // inside Dashboard handles no-plan / past-due / cancelled states.
            user.role === 'client' ? (
              !user.firm?.onboarding_completed ? <Navigate to="/onboarding" /> :
              <Dashboard 
                user={user} 
                onLogout={handleFullLogout}
                selectedClient={null}
                onSwitchClient={null}
              />
            ) : (
              // For admin/internal team, allow blog routes without client selection
              (selectedClient || window.location.pathname.startsWith('/dashboard/blogs')) ? (
                <Dashboard 
                  user={user} 
                  onLogout={handleFullLogout}
                  selectedClient={selectedClient}
                  onSwitchClient={handleSwitchClient}
                />
              ) : (
                <Navigate to={user.role === 'super_admin' ? '/admin' : '/my-clients'} />
              )
            )
          ) : <Navigate to="/auth" />
        } 
      />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const firmData = localStorage.getItem('firm');
    
    if (token && userData && firmData) {
      setUser({
        ...JSON.parse(userData),
        firm: JSON.parse(firmData)
      });
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData, firmData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('firm', JSON.stringify(firmData));
    setUser({ ...userData, firm: firmData });
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-primary text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes 
          user={user} 
          setUser={setUser}
          handleLogin={handleLogin} 
          handleLogout={handleLogout} 
        />
      </BrowserRouter>
    </div>
  );
}

export default App;